/**
 * Preview Scale Component
 * Generic zoom controls for preview panel
 */

import React from 'react';
import { Button } from '@/core/components/ui/button';
import { Label } from '@/core/components/ui/label';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { useZoomControls, ZOOM_PRESETS } from '../../stores/device-preview-store';

interface PreviewScaleProps {
  /** Optional CSS class */
  className?: string;
}

/**
 * Preview Scale Component
 * Provides zoom controls for device preview
 */
export function PreviewScale({ className = '' }: PreviewScaleProps) {
  const { scale, zoomIn, zoomOut, resetZoom, fitToScreen, canZoomIn, canZoomOut } =
    useZoomControls();

  const scalePercentage = Math.round(scale * 100);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Label className="text-xs text-muted-foreground">Zoom:</Label>

      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={zoomOut}
          disabled={!canZoomOut}
          title="Zoom out"
        >
          <ZoomOut className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={resetZoom}
          className="min-w-[60px]"
          title="Reset zoom"
        >
          <span className="text-xs font-medium">{scalePercentage}%</span>
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={zoomIn}
          disabled={!canZoomIn}
          title="Zoom in"
        >
          <ZoomIn className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={fitToScreen}
          title="Fit to screen"
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
